import { observer } from "mobx-react-lite";
import { useIntl } from "react-intl";
import { ControlPoint, SectionData } from "../../services/PlansService";
import { convertDistance, convertElevation, getLatLngForDistance } from "../../utils/UnitsUtils";
import { useContext, useEffect, useState } from "react";
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { AppContext } from "../../contexts/AppContext";
import Check from "@mui/icons-material/Check";
import Edit from "@mui/icons-material/Edit";
import styled from "@mui/material/styles/styled";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { SurfaceData } from "../../utils/getSurfaceData";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import NordicWalking from "@mui/icons-material/NordicWalking";
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import WbSunnyOutlined from "@mui/icons-material/WbSunnyOutlined";
import MapOutlined from "@mui/icons-material/MapOutlined";
import { SurfaceDataBox } from "./SurfaceDataBox";
import posthog from "posthog-js";
import { useNavigate } from "react-router-dom";
import { fetchWeatherForLocation, uvRisk, WeatherData } from "../../utils/WeatherUtils";

dayjs.extend(localizedFormat);

const ParagraphLine = styled("span")(({ theme }) => ({
    verticalAlign: "baseline",
    height: 1,
    flexGrow: 1,
    flexShrink: 1,
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    marginLeft: "0.5em",
    marginRight: "0.5em"
}));

const ArrowLine = styled("span")(({ theme }) => ({
    verticalAlign: "center",
    height: 1,
    flexGrow: 1,
    flexShrink: 1,
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    marginLeft: "0.5em",
}));

const ArrowPoint = styled("span")(({ theme }) => ({
    width: 0,
    height: 0,
    borderTop: "5px solid transparent",
    borderBottom: "5px solid transparent",
    borderLeft: `10px solid ${theme.palette.grey[400]}`,
    marginRight: "0.5em"
}));

const PlannerTab = styled(Tab)(() => ({
    textTransform: "none",
    minHeight: 48,
    position: "relative",
}));

type SectionTableTab = "info" | "terrain" | "weather";

export interface SectionTableProps {
    planId: string;
    currentSection: number;
    sectionData: SectionData;
    surfaceData?: SurfaceData[];
    onSaveControlPoint: (cpIndex: number, cpData: ControlPoint) => void;
    readOnly?: boolean;
    startLatlng?: number[];
    endLatlng?: number[];
}

const SectionTable: React.FC<SectionTableProps> = observer(({ planId, currentSection, sectionData, surfaceData, onSaveControlPoint, readOnly, startLatlng, endLatlng }) => {

    const intl = useIntl();
    const { userService, plansService } = useContext(AppContext);
    const navigate = useNavigate();

    const distanceUnits = userService.user?.distanceUnits || "km";
    const elevationUnits = userService.user?.elevationUnits || "m";
    const temperatureUnits = userService.user?.temperatureUnits || "C";

    const convertTemp = (celsius: number) =>
        temperatureUnits === "F" ? Math.round(celsius * 9 / 5 + 32) : Math.round(celsius);
    const tempUnit = `°${temperatureUnits}`;

    const convertSpeed = (kmh: number) =>
        distanceUnits === "mi" ? Math.round(kmh * 0.621371) : Math.round(kmh);
    const speedUnit = distanceUnits === "mi" ? "mph" : "km/h";

    const [currentName, setCurrentName] = useState<string>(sectionData.name || "");
    const [currentFood, setCurrentFood] = useState<boolean>(sectionData.food || false);
    const [currentDrinks, setCurrentDrinks] = useState<boolean>(sectionData.drinks || false);
    const [currentWc, setCurrentWc] = useState<boolean>(sectionData.wc || false);
    const [currentDropbag, setCurrentDropbag] = useState<boolean>(sectionData.dropbag || false);
    const [currentCrew, setCurrentCrew] = useState<boolean>(sectionData.crew || false);

    const [sectionTableState, setSectionTableState] = useState<SectionTableTab>("info");
    const [editing, setEditing] = useState<boolean>(false);

    const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [weatherErrorKey, setWeatherErrorKey] = useState<string | null>(null);
    const [weatherArrivalTime, setWeatherArrivalTime] = useState<Date | null>(null);

    useEffect(() => {
        setCurrentName(sectionData.name || "");
        setCurrentFood(sectionData.food || false);
        setCurrentDrinks(sectionData.drinks || false);
        setCurrentWc(sectionData.wc || false);
        setCurrentDropbag(sectionData.dropbag || false);
        setCurrentCrew(sectionData.crew || false);
    }, [sectionData]);

    useEffect(() => {
        // Terrain tab is only valid for section type; fall back to info for control points
        if (sectionTableState === "terrain" && sectionData.type !== "section") {
            setSectionTableState("info");
        }
        setEditing(false);
        setWeatherData(null);
        setWeatherLoading(false);
        setWeatherErrorKey(null);
        setWeatherArrivalTime(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSection]);

    useEffect(() => {
        if (sectionTableState !== "weather") return;

        setWeatherLoading(true);
        setWeatherErrorKey(null);
        setWeatherData(null);

        if (!plansService.latlng.length || !plansService.distancePoints.length) {
            setWeatherLoading(false);
            setWeatherErrorKey("plans.weatherNoRoute");
            return;
        }

        const planDate = plansService.plan?.date ? new Date(plansService.plan.date) : null;
        if (!planDate) {
            setWeatherLoading(false);
            setWeatherErrorKey("plans.weatherNoDate");
            return;
        }

        // Time elapsed before reaching this section
        const plannedEffort = plansService.plan?.plannedEffort || [];
        const minutesBefore = plannedEffort
            .slice(0, currentSection)
            .reduce((acc, e) => acc + (e?.time ?? 0), 0);

        // For sections: use midpoint time (runner is in it for the full section duration)
        // For control points: use arrival time (runner passes through briefly)
        const sectionMinutes = sectionData.type === "section"
            ? (plannedEffort[currentSection]?.time ?? 0) / 2
            : 0;
        const arrivalTime = new Date(planDate.getTime() + (minutesBefore + sectionMinutes) * 60 * 1000);

        // MET Norway forecast covers ~10 days ahead only — no historical data
        const now = new Date();
        const daysDiff = (arrivalTime.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        if (daysDiff < 0) {
            setWeatherLoading(false);
            setWeatherErrorKey("plans.weatherPastDate");
            return;
        }
        if (daysDiff > 10) {
            setWeatherLoading(false);
            setWeatherErrorKey("plans.weatherTooFarFuture");
            return;
        }

        setWeatherArrivalTime(arrivalTime);

        const midDistanceKm = ((sectionData.startDistance + sectionData.endDistance) / 2) / 1000;
        const [lng, lat] = getLatLngForDistance(midDistanceKm, plansService.latlng, plansService.distancePoints);

        fetchWeatherForLocation(lat, lng, arrivalTime)
            .then(data => {
                if (data) {
                    setWeatherData(data);
                } else {
                    setWeatherErrorKey("plans.weatherError");
                }
                setWeatherLoading(false);
            })
            .catch(() => {
                setWeatherErrorKey("plans.weatherError");
                setWeatherLoading(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionTableState, currentSection, plansService.plan?.date?.getTime()]);

    const editControlPoint = (section: SectionData) => {
        if (section.type === "control") {
            navigate(`/edit/${userService.user?.id}/${planId}`);
        } else {
            setEditing(true);
        }
    };

    const handleSave = () => {
        posthog.capture('control_point_saved');
        const controlPoint = {
            ...sectionData,
            name: currentName,
            food: currentFood,
            drinks: currentDrinks,
            wc: currentWc,
            dropbag: currentDropbag,
            crew: currentCrew
        } as ControlPoint;
        onSaveControlPoint(Math.floor(currentSection / 2), controlPoint);
        setEditing(false);
    };

    const mapsHref = sectionData.type === "start" && startLatlng
        ? `https://www.google.com/maps/search/?api=1&query=${startLatlng[1]},${startLatlng[0]}`
        : sectionData.type === "end" && endLatlng
            ? `https://www.google.com/maps/search/?api=1&query=${endLatlng[1]},${endLatlng[0]}`
            : null;

    const isSection = sectionData.type === "section";

    return (
        <>
            {/* Tabs — terrain tab only shown for section type */}
            <Tabs
                value={sectionTableState}
                onChange={(_, value) => setSectionTableState(value as SectionTableTab)}
                variant="fullWidth"
                aria-label="section info tabs"
            >
                {sectionTableState === "info"
                    ? <PlannerTab icon={<InfoOutlined />} iconPosition="start" value="info" />
                    : <Tooltip title={intl.formatMessage({ id: "plans.stats" })} placement="top">
                        <PlannerTab icon={<InfoOutlined />} iconPosition="start" value="info" />
                    </Tooltip>
                }
                {isSection && (sectionTableState === "terrain"
                    ? <PlannerTab icon={<NordicWalking />} iconPosition="start" value="terrain" />
                    : <Tooltip title={intl.formatMessage({ id: "plans.terrain" })} placement="top">
                        <PlannerTab icon={<NordicWalking />} iconPosition="start" value="terrain" />
                    </Tooltip>
                )}
                {sectionTableState === "weather"
                    ? <PlannerTab icon={<WbSunnyOutlined />} iconPosition="start" value="weather" />
                    : <Tooltip title={intl.formatMessage({ id: "plans.weather" })} placement="top">
                        <PlannerTab icon={<WbSunnyOutlined />} iconPosition="start" value="weather" />
                    </Tooltip>
                }
            </Tabs>

            {/* Tab 1 — Info: Stats + Services + Before/After */}
            {sectionTableState === "info" && (
                <Box marginY={1} paddingX={2}>

                    {/* Control / start / end: name + services */}
                    {!isSection && (
                        <Box marginY={0.5}>
                            {editing ? (
                                <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
                                    <Box marginRight={1}>
                                        <TextField
                                            margin="none"
                                            required
                                            fullWidth
                                            id="controlPointName"
                                            name="controlPointName"
                                            value={currentName}
                                            autoFocus
                                            onChange={(e) => setCurrentName(e.target.value)}
                                            size="small"
                                        />
                                    </Box>
                                    <IconButton onClick={handleSave} size="small">
                                        <Check fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
                                    <Typography variant="body2" fontWeight="bold">{
                                        currentName ? currentName :
                                            sectionData.type === "start" ? intl.formatMessage({ id: "plans.start" }) :
                                                sectionData.type === "end" ? intl.formatMessage({ id: "plans.end" }) :
                                                    `${intl.formatMessage({ id: "plans.CP" })} ${Math.ceil(currentSection / 2)}`
                                    }</Typography>
                                    {!readOnly && (
                                        <Tooltip title={intl.formatMessage({ id: sectionData.type === "control" ? "plans.editControlPoints" : "plans.editControlPointName" })}>
                                            <IconButton aria-label="edit" size="small" onClick={() => editControlPoint(sectionData)}>
                                                <Edit fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )}

                            <Box textAlign="center" marginBottom={0.5}>
                                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.servicesTitle" })}</Typography>
                            </Box>

                            {editing ? (
                                <Box display="flex" gap={1} flexDirection="column">
                                    <FormControlLabel control={<Checkbox checked={currentFood} onChange={(e) => setCurrentFood(e.target.checked)} />} label={intl.formatMessage({ id: "data.food" })} />
                                    <FormControlLabel control={<Checkbox checked={currentDrinks} onChange={(e) => setCurrentDrinks(e.target.checked)} />} label={intl.formatMessage({ id: "data.drinks" })} />
                                    <FormControlLabel control={<Checkbox checked={currentWc} onChange={(e) => setCurrentWc(e.target.checked)} />} label={intl.formatMessage({ id: "data.wc" })} />
                                    <FormControlLabel control={<Checkbox checked={currentDropbag} onChange={(e) => setCurrentDropbag(e.target.checked)} />} label={intl.formatMessage({ id: "data.dropbag" })} />
                                    <FormControlLabel control={<Checkbox checked={currentCrew} onChange={(e) => setCurrentCrew(e.target.checked)} />} label={intl.formatMessage({ id: "data.crew" })} />
                                </Box>
                            ) : (
                                <>
                                    {currentFood && <Chip sx={{ margin: 0.5 }} label={intl.formatMessage({ id: "data.foodLabel" })} />}
                                    {currentDrinks && <Chip sx={{ margin: 0.5 }} label={intl.formatMessage({ id: "data.drinksLabel" })} />}
                                    {currentWc && <Chip sx={{ margin: 0.5 }} label={intl.formatMessage({ id: "data.wcLabel" })} />}
                                    {currentDropbag && <Chip sx={{ margin: 0.5 }} label={intl.formatMessage({ id: "data.dropbagLabel" })} />}
                                    {currentCrew && <Chip sx={{ margin: 0.5 }} label={intl.formatMessage({ id: "data.crewLabel" })} />}
                                    {!currentFood && !currentDrinks && !currentWc && !currentDropbag && !currentCrew && (
                                        <Box textAlign="center" marginY={1}>
                                            <Typography variant="body2" color="grey" fontStyle="italic">{intl.formatMessage({ id: "data.noServices" })}</Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    )}

                    {/* Section: distance markers + numeric stats */}
                    {isSection && (
                        <Box display="flex" alignItems="center" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertDistance(sectionData.startDistance / 1000, distanceUnits), unit: intl.formatMessage({ id: distanceUnits }) })}</Typography>
                            <ArrowLine />
                            <ArrowPoint />
                            <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertDistance(sectionData.endDistance / 1000, distanceUnits), unit: intl.formatMessage({ id: distanceUnits }) })}</Typography>
                        </Box>
                    )}

                    {isSection && (
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.distanceTitle" })}</Typography>
                            <ParagraphLine />
                            <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertDistance(sectionData.distance / 1000, distanceUnits), unit: intl.formatMessage({ id: distanceUnits }) })}</Typography>
                        </Box>
                    )}

                    {sectionData.type === "control" && (
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.distanceToCPTitle" })}</Typography>
                            <ParagraphLine />
                            <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertDistance(sectionData.startDistance / 1000, distanceUnits), unit: intl.formatMessage({ id: distanceUnits }) })}</Typography>
                        </Box>
                    )}

                    {!!sectionData.elevationGain && (
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.elevationGainTitle" })}</Typography>
                            <ParagraphLine />
                            <Typography variant="body2" fontWeight="bold">+{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(sectionData.elevationGain, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                        </Box>
                    )}

                    {!!sectionData.elevationLoss && (
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.elevationLossTitle" })}</Typography>
                            <ParagraphLine />
                            <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(sectionData.elevationLoss, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                        </Box>
                    )}

                    {isSection ? (
                        <>
                            <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                                <Typography variant="body2">{intl.formatMessage({ id: "data.minElevationTitle" })}</Typography>
                                <ParagraphLine />
                                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(sectionData.minElevation, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                                <Typography variant="body2">{intl.formatMessage({ id: "data.maxElevationTitle" })}</Typography>
                                <ParagraphLine />
                                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(sectionData.maxElevation, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                            </Box>
                            {sectionData.avgUphillGrade != null && sectionData.avgUphillGrade !== 0 && (
                                <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                                    <Typography variant="body2">{intl.formatMessage({ id: "data.avgUphillGradeTitle" })}</Typography>
                                    <ParagraphLine />
                                    <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.gradeValue" }, { value: Math.round(sectionData.avgUphillGrade) })}</Typography>
                                </Box>
                            )}
                            {sectionData.avgDownhillGrade != null && sectionData.avgDownhillGrade !== 0 && (
                                <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                                    <Typography variant="body2">{intl.formatMessage({ id: "data.avgDownhillGradeTitle" })}</Typography>
                                    <ParagraphLine />
                                    <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.gradeValue" }, { value: Math.round(sectionData.avgDownhillGrade) })}</Typography>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.elevationTitle" })}</Typography>
                            <ParagraphLine />
                            <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(sectionData.minElevation, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                        </Box>
                    )}

                    {/* Before / After */}
                    {sectionData.beforeDistance > 0 && (
                        <Box marginTop={2}>
                            <Box display="flex" alignItems="center" gap={1} marginBottom={0.5}>
                                <TransferWithinAStationIcon fontSize="small" color="action" />
                                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.beforeTitle" })}</Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" justifyContent="space-between">
                                <Typography variant="body2">{intl.formatMessage({ id: "data.distanceTitle" })}</Typography>
                                <ParagraphLine />
                                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertDistance(sectionData.beforeDistance / 1000, distanceUnits), unit: intl.formatMessage({ id: distanceUnits }) })}</Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={0.5}>
                                <Typography variant="body2">{intl.formatMessage({ id: "data.elevationGainTitle" })}</Typography>
                                <ParagraphLine />
                                <Typography variant="body2" fontWeight="bold">+{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(sectionData.beforeElevationGain, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={0.5}>
                                <Typography variant="body2">{intl.formatMessage({ id: "data.elevationLossTitle" })}</Typography>
                                <ParagraphLine />
                                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(sectionData.beforeElevationLoss, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                            </Box>
                        </Box>
                    )}

                    {sectionData.afterDistance > 0 && (
                        <Box marginTop={2}>
                            <Box display="flex" alignItems="center" gap={1} marginBottom={0.5}>
                                <TransferWithinAStationIcon fontSize="small" color="action" />
                                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.afterTitle" })}</Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" justifyContent="space-between">
                                <Typography variant="body2">{intl.formatMessage({ id: "data.distanceTitle" })}</Typography>
                                <ParagraphLine />
                                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertDistance(sectionData.afterDistance / 1000, distanceUnits), unit: intl.formatMessage({ id: distanceUnits }) })}</Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={0.5}>
                                <Typography variant="body2">{intl.formatMessage({ id: "data.elevationGainTitle" })}</Typography>
                                <ParagraphLine />
                                <Typography variant="body2" fontWeight="bold">+{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(sectionData.afterElevationGain, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={0.5}>
                                <Typography variant="body2">{intl.formatMessage({ id: "data.elevationLossTitle" })}</Typography>
                                <ParagraphLine />
                                <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(sectionData.afterElevationLoss, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Google Maps for start / end */}
                    {mapsHref && (
                        <Box marginTop={2}>
                            <Button startIcon={<MapOutlined />} variant="contained" color="primary" target="_blank" href={mapsHref} fullWidth>
                                {intl.formatMessage({ id: "plans.openOnGoogleMaps" })}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {/* Tab 2 — Terrain & Surface (section type only) */}
            {sectionTableState === "terrain" && isSection && (
                <Box marginY={2} paddingX={2}>
                    {surfaceData && surfaceData.length > 0 ? (
                        <SurfaceDataBox surfaceData={surfaceData} sectionData={sectionData} />
                    ) : (
                        <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
                            <NordicWalking sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                {intl.formatMessage({ id: "plans.noTerrainData" })}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Tab 3 — Weather (live from Open-Meteo) */}
            {sectionTableState === "weather" && (
                <Box paddingX={2} marginY={2}>
                    {weatherLoading && (
                        <Box display="flex" justifyContent="center" mt={4}>
                            <CircularProgress size={32} />
                        </Box>
                    )}
                    {!weatherLoading && weatherErrorKey && (
                        <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
                            <WbSunnyOutlined sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                {intl.formatMessage({ id: weatherErrorKey })}
                            </Typography>
                        </Box>
                    )}
                    {!weatherLoading && !weatherErrorKey && weatherData && (
                        <>
                            {/* Condition header */}
                            <Box display="flex" alignItems="center" gap={2} marginBottom={2}>
                                <Typography fontSize={36} lineHeight={1}>{weatherData.icon}</Typography>
                                <Box>
                                    {weatherData.temperature !== null && (
                                        <Typography variant="h6" lineHeight={1}>{convertTemp(weatherData.temperature)}{tempUnit}</Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">{intl.formatMessage({ id: weatherData.conditionKey })}</Typography>
                                </Box>
                            </Box>

                            {weatherData.humidity !== null && (
                                <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                                    <Typography variant="body2">{intl.formatMessage({ id: "data.humidity" })}</Typography>
                                    <ParagraphLine />
                                    <Typography variant="body2" fontWeight="bold">{weatherData.humidity}%</Typography>
                                </Box>
                            )}

                            {weatherData.uvIndex !== null && (() => {
                                const risk = uvRisk(weatherData.uvIndex!);
                                const riskColor =
                                    risk === "low" ? "success.main" :
                                    risk === "moderate" ? "warning.main" : "error.main";
                                return (
                                    <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                                        <Typography variant="body2">{intl.formatMessage({ id: "data.uvIndex" })}</Typography>
                                        <ParagraphLine />
                                        <Box display="flex" alignItems="baseline" gap={0.5}>
                                            <Typography variant="body2" fontWeight="bold">{weatherData.uvIndex!.toFixed(1)}</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: riskColor }}>
                                                {intl.formatMessage({ id: `data.uvRisk.${risk}` })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })()}

                            {weatherData.windspeed !== null && (
                                <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                                    <Typography variant="body2">{intl.formatMessage({ id: "data.wind" })}</Typography>
                                    <ParagraphLine />
                                    <Typography variant="body2" fontWeight="bold">{convertSpeed(weatherData.windspeed)} {speedUnit}</Typography>
                                </Box>
                            )}
                            {weatherData.windDirectionLabel !== null && (
                                <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                                    <Typography variant="body2">{intl.formatMessage({ id: "data.windDirection" })}</Typography>
                                    <ParagraphLine />
                                    <Typography variant="body2" fontWeight="bold">{weatherData.windDirectionLabel} ({weatherData.windDirectionDeg}°)</Typography>
                                </Box>
                            )}

                            {weatherData.precipitation !== null && (
                                <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                                    <Typography variant="body2">{intl.formatMessage({ id: "data.precipitation" })}</Typography>
                                    <ParagraphLine />
                                    <Typography variant="body2" fontWeight="bold">{weatherData.precipitation} mm</Typography>
                                </Box>
                            )}

                            {weatherArrivalTime && (
                                <Typography variant="caption" color="text.secondary" display="block" marginTop={2}>
                                    {intl.formatMessage({ id: "plans.weatherForecastAt" }, { date: dayjs(weatherArrivalTime).locale(intl.locale).format("lll") })}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            )}
        </>
    );
});

export default SectionTable;
